import { Component, Input } from '@angular/core';
import { SmsTextComponent } from '../sms-text/sms-text.component';
import { SmsParts, describeChar, smsParts } from '../../../../utils/sms';

/**
 * A sent message drawn the way a phone shows it, with a ruler underneath
 * that fills one capsule per SMS part. Length decides cost, so the ruler
 * makes the cost of the copy visible at a glance.
 */
@Component({
  selector: 'bk-sms-bubble',
  standalone: true,
  imports: [SmsTextComponent],
  templateUrl: './sms-bubble.component.html',
  styleUrl: './sms-bubble.component.css',
})
export class SmsBubbleComponent {
  copy = '';
  parts: SmsParts = smsParts('');

  @Input() set text(value: string | null | undefined) {
    this.copy = value ?? '';
    this.parts = smsParts(this.copy);
  }

  /** 'hero' sizes to the copy, 'compact' is 15px, 'small' is 13px (body text beside it). */
  @Input() variant: 'hero' | 'compact' | 'small' = 'hero';
  /** Lines shown before the copy is cut; 0 shows the whole message. */
  @Input() clamp = 7;
  /** Shown when there is no copy yet (e.g. "Start typing to preview"). */
  @Input() emptyText = 'No message text on record for this send.';
  /** The copy is final (placeholders already filled): drop the "About" estimate wording. */
  @Input() exact = false;

  /** Long copy steps down a size so the bubble keeps its shape. */
  get size(): 'xl' | 'lg' | 'md' | 'sm' | 'xs' {
    if (this.variant === 'small') return 'xs';
    if (this.variant === 'compact') return 'sm';
    const length = this.copy.length;
    if (length <= 100) return 'xl';
    if (length <= 190) return 'lg';
    return 'md';
  }

  get lengthLabel(): string {
    const { length, parts, perPart, estimated } = this.parts;
    const about = estimated && !this.exact ? 'About ' : '';
    return parts <= 1
      ? `${about}${length} of ${perPart} characters`
      : `${about}${length} characters`;
  }

  get partsLabel(): string {
    const { parts } = this.parts;
    return parts === 1 ? '1 part' : `${parts} parts`;
  }

  /** Only Unicode needs a note: it is the case that quietly raises cost. */
  get encodingLabel(): string | null {
    const { encoding, offender } = this.parts;
    if (encoding !== 'Unicode') return null;
    return offender ? `Unicode, because of ${describeChar(offender)}` : 'Unicode';
  }
}
